/**
 * Cliente HTTP único de Nebula POS.
 *
 * Antes, cada página/componente declaraba su propio
 * `const API_URL = 'http://localhost:8000'` y llamaba a `fetch` directo: 82
 * llamadas duplicadas en 27 archivos. Eso obligaba a recompilar para cambiar de
 * backend y hacía imposible hablar con un backend por cliente.
 *
 * El frontend tiene exactamente dos requests contra el Nebula Manager, ambos en
 * `login()`: `/api/auth/login` (usuario + contraseña → llave de la sucursal) y
 * `/api/auth/metadata` (llave + usuario → vistas permitidas). Con eso tiene lo
 * que necesita para pintar la interfaz.
 *
 * Todo lo demás va al backend de POS con la llave en `X-Nebula-Key`. El backend
 * es la capa de control y seguridad: antes de atender cada request llama a
 * `/api/auth/conexion` para saber a qué base de datos conectarse. El navegador
 * nunca ve una conexión ni una contraseña, solo la respuesta final de cada
 * endpoint.
 *
 * Configuración (variables de CRA; reiniciar `npm start` tras cambiarlas):
 *   REACT_APP_MANAGER_URL  URL del Nebula Manager (login y metadata)
 *   REACT_APP_API_URL      Backend de POS
 *   REACT_APP_TENANT_KEY_HEADER  Cabecera de la llave (por defecto X-Nebula-Key)
 */

const MANAGER_URL = (process.env.REACT_APP_MANAGER_URL || '').replace(/\/+$/, '');
const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');
const KEY_HEADER = process.env.REACT_APP_TENANT_KEY_HEADER || 'X-Nebula-Key';
const TENANT_TIMEOUT_MS = 10000;

const STORAGE_KEY = 'nebula.session';

/**
 * Sesión activa. `null` mientras no se haya hecho login.
 * @type {{apiKey: string|null, username: string, userName: string|null,
 *         storeName: string|null, branchId: number|null, views: string[]}|null}
 */
let session = loadSession();

function loadSession() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.apiKey ? parsed : null;
  } catch {
    return null;
  }
}

function saveSession(next) {
  session = next;
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Modo privado o storage lleno: la sesión vive solo en memoria.
  }
}

export function getSession() {
  return session;
}

export function clearSession() {
  saveSession(null);
}

/**
 * URL absoluta de un endpoint del backend de esta sesión.
 * Para `fetch`, `EventSource` o `<img src>`: ahí no se puede poner la cabecera.
 */
export function apiUrl(path) {
  return `${BACKEND_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Cabeceras comunes: la key del cliente es lo que autoriza la petición. */
function authHeaders(extra = {}) {
  const headers = { ...extra };
  if (session && session.apiKey) {
    headers[KEY_HEADER] = session.apiKey;
  }
  return headers;
}

async function requestJson(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TENANT_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const text = await response.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!response.ok) {
      const error = new Error(
        (data && data.detail) || `HTTP ${response.status} en ${url}`,
      );
      error.status = response.status;
      throw error;
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
}
/**
 * `fetch` con la base del backend de la sesión y la cabecera de la API key.
 *
 * Devuelve la `Response` tal cual, igual que `fetch`: los ~80 call sites del
 * proyecto ya esperan `res.ok` / `res.json()` y así no hay que tocar ninguno.
 * Para obtener el JSON ya parseado usa `apiJson()`.
 */
export async function apiFetch(path, options = {}) {
  const { headers, ...rest } = options;
  const res = await fetch(apiUrl(path), { ...rest, headers: authHeaders(headers) });
  if (res.status === 401) {
    clearSession();
    // No recargar automáticamente: evita perder el contexto al debuggear
    // if (typeof window !== 'undefined' && window.location) {
    //   window.location.reload();
    // }
  }
  return res;
}

/**
 * Igual que `apiFetch` pero devuelve el cuerpo ya parseado y lanza `Error` con
 * el `detail` de FastAPI si la respuesta no es 2xx.
 */
export async function apiJson(path, options = {}) {
  const response = await apiFetch(path, options);
  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!response.ok) {
    const error = new Error((data && data.detail) || `HTTP ${response.status} en ${path}`);
    error.status = response.status;
    if (response.status === 403) {
      try {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(error.message || 'Sin permisos para esta acción');
        }
      } catch {}
    }
    throw error;
  }
  return data;
}

/**
 * Inicia sesión contra el Nebula Manager.
 *
 * Dos requests, los dos únicos que el navegador le hace al Manager:
 *
 *   1. `POST /api/auth/login {usuario, password}` → la llave de la sucursal y los
 *      datos del usuario.
 *   2. `POST /api/auth/metadata {llave, usuario}` → las vistas que ese usuario
 *      tiene autorizadas y su información.
 *
 * A partir de ahí el frontend solo habla con el backend de POS: lleva la llave
 * en `X-Nebula-Key` y el backend llama por su cuenta a `/api/auth/conexion` para
 * saber a qué base conectarse. El navegador nunca pide conexiones.
 *
 * La llave y el usuario quedan en la sesión porque metadata los necesita: la
 * llave identifica la sucursal y el usuario determina qué vistas trae.
 *
 * @param {{username: string, password: string}} credentials
 * @returns {Promise<object>} La sesión guardada.
 */
export async function login(credentials) {
  const auth = await requestJson(`${MANAGER_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      usuario: credentials.username,
      password: credentials.password,
    }),
  });

  if (!auth || auth.autorizado !== true) {
    throw new Error((auth && auth.motivo) || 'Credenciales no válidas');
  }

  const username = (auth.usuario && auth.usuario.username) ? auth.usuario.username : credentials.username;

  const views = Array.isArray(auth.vistas_permitidas)
    ? auth.vistas_permitidas.map(v => typeof v === 'string' ? v : v.slug).filter(Boolean)
    : [];

  saveSession({
    apiKey: auth.llave || null,
    username,
    storeName: auth.sucursal || null,
    branchId: auth.sucursal_id ?? null,
    userName: (auth.usuario && auth.usuario.full_name) ? auth.usuario.full_name : username,
    views,
  });

  // No llamamos a /auth/tenant/resolve automáticamente: el backend ya valida
  // con la llave; evitamos llamadas innecesarias. Si hace falta, la app lo consulta.
  return getSession();
}

/**
 * Pregunta al Manager qué puede ver este usuario en esta sucursal.
 * También sirve para rehidratar la sesión al recargar la página.
 */
export async function loadSessionContext() {
  if (!session || !session.apiKey || !session.username) return session;

  // Intentar con llave+usuario; si no hay usuario, no llamamos metadata
  if (!session.apiKey) return session;
  const body = session.username ? { llave: session.apiKey, usuario: session.username } : { llave: session.apiKey };
  const meta = await requestJson(`${MANAGER_URL}/api/auth/metadata`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!meta || meta.autorizado !== true) {
    // El Manager acepta la llave pero no reconoce al usuario para esa sucursal.
    clearSession();
    throw new Error((meta && meta.motivo) || 'Usuario no autorizado para esta sucursal');
  }

  saveSession({
    ...session,
    storeName: meta.sucursal || session.storeName,
    branchId: meta.sucursal_id ?? session.branchId,
    userName: (meta.usuario && meta.usuario.full_name) || session.userName,
    views: Array.isArray(meta.vistas_permitidas)
      ? meta.vistas_permitidas.map((v) => (typeof v === 'string' ? v : v.slug)).filter(Boolean)
      : [],
  });
  return session;
}

/**
 * Confirma que el backend acepta la llave y que la base de la sucursal responde.
 * Distingue "credencial mala" de "servicio caído", que el login necesita.
 */
export async function checkBackend() {
  return apiJson('/auth/tenant/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

/**
 * Permisos del Manager (database/init.sql) traducidos a las claves de
 * navegación del frontend (App.js). El slug es el vocabulario del Manager; la
 * clave de navegación es el del POS, y no tienen por qué coincidir uno a uno.
 */
const VIEW_TO_PAGE = {
  punto_de_venta: 'inicio',
  comandero: 'comandero',
  gestion_pedidos: 'ordenes',
  gestion_articulos: 'productos',
  promociones_categoria: 'categorias',
  inventario: 'inventario',
  registro_ventas: 'finanzas',
  gastos: 'gastos',
  gestion_diaria: 'calendario',
  configuraciones: 'configuracion',
  barra: 'barra',
  cocina: 'cocina',
};

/**
 * Las páginas de navegación en el orden en que las muestra la barra. La primera
 * a la que puede entrar el usuario es su página de inicio: si no tiene el punto
 * de venta, entrar ahí de rebote lo dejaría viendo una pantalla prohibida.
 */
export const NAV_PAGES = [
  'inicio',
  'comandero',
  'ordenes',
  'productos',
  'categorias',
  'inventario',
  'finanzas',
  'gastos',
  'calendario',
  'configuracion',
  'barra',
  'cocina',
];

/**
 * ¿El usuario tiene acceso a esta vista?
 *
 * `vistas_permitidas` no es una lista de sugerencias: es el padrón de acceso
 * completo. Si viene vacía, el usuario no tiene ninguna vista, y por eso se
 * cierra en vez de abrir: una lista vacía significa "nada", nunca "todo".
 *
 * También se cierra cuando no hay sesión, o cuando la sesión guardada no trae
 * `views` (una sesión vieja en localStorage, de antes de que existiera el
 * campo). Ante la duda, sin acceso.
 *
 * Ojo: esto solo esconde botones en la UI. La autorización real la aplica el
 * backend, que es quien consulta al Manager antes de cada request.
 */
export function canAccess(page) {
  // En etapa de maquetado, Barra y Cocina están disponibles sin depender
  // de los permisos del Manager.
  if (page === 'barra' || page === 'cocina' || page === 'reporte') return true;
  if (!session || !Array.isArray(session.views)) return false;
  // El comandero de bolsillo se limita desde el Manager con la vista
  // `comandero`; quien tenga el punto de venta también puede entrar.
  if (page === 'comandero') {
    return session.views.some((slug) => VIEW_TO_PAGE[slug] === 'comandero' || VIEW_TO_PAGE[slug] === 'inicio');
  }
  return session.views.some((slug) => VIEW_TO_PAGE[slug] === page);
}

/**
 * Primera página de `NAV_PAGES` que este usuario puede ver, o `null` si no tiene
 * ninguna. Nunca se recurre a `inicio` a ciegas: si los permisos vinieron mal
 * configurados, devolver `inicio` metería al usuario en el punto de venta
 * justamente cuando no tiene derecho a él.
 */
export function firstAllowedPage() {
  return NAV_PAGES.find((page) => canAccess(page)) || null;
}

export { KEY_HEADER, MANAGER_URL, BACKEND_URL };
